<template>
  <div class="story-practice-page">
    <div id="iRecorder" class="recorder-host"></div>

    <section v-if="loading" class="center-state">
      <p>加载中...</p>
    </section>

    <section v-else-if="loadError" class="center-state">
      <p>{{ loadError }}</p>
      <button class="pill-btn" @click="initPage">重试</button>
    </section>

    <transition name="scene-switch" mode="out-in">
      <section
        v-if="mode === 'story' && hasAnyStoryPages"
        key="story-pages"
        class="story-page-stage"
        :class="{ cover: storyScreen === 'cover' && hasCover }"
      >
        <div class="story-page-canvas" :style="storyPageStageStyle">
          <template v-if="storyScreen === 'cover' && hasCover">
            <div class="story-cover-fade"></div>
            <button class="start-reading-btn" @click="startReadingPages">
              开始阅读
            </button>
          </template>

          <template v-else>
            <button
              v-for="asset in currentStoryPage.assets"
              :key="asset.id"
              class="story-page-asset"
              :class="`enter-${asset.enterAnimation || 'left'}`"
              :style="storyAssetStyle(asset)"
              @click="handleStoryAssetClick(asset)"
            >
              <img :src="asset.imageUrl" alt="" />
            </button>

            <button
              v-if="currentStoryPageAudioUrl"
              class="page-audio-btn"
              :class="{ playing: storyAudioPlaying }"
              aria-label="播放页面音频"
              @click="toggleStoryPageAudio"
            >
              <span class="page-audio-icon">
                <img
                  class="page-audio-step page-audio-step1"
                  src="../assets/audio-step1-icon.png"
                  alt=""
                />
                <img
                  class="page-audio-step page-audio-step2"
                  :class="{ visible: showStoryAudioStep2 }"
                  src="../assets/audio-step2-icon.png"
                  alt=""
                />
                <img
                  class="page-audio-step page-audio-step3"
                  :class="{ visible: showStoryAudioStep3 }"
                  src="../assets/audio-step3-icon.png"
                  alt=""
                />
              </span>
            </button>

            <button class="page-next-btn" @click="goNextStoryPage">
              <img src="../assets/page-next-arrow.png" alt="下一页" />
            </button>
          </template>
        </div>
      </section>

      <section
        v-else-if="mode === 'practice'"
        :key="practiceScreen"
        class="practice-stage"
      >
        <button
          class="skip-segment-btn"
          :disabled="recordStatus === 'recording' || recordStatus === 'scoring'"
          @click="skipPracticeSegment"
        >
          跳过
        </button>

        <template v-if="practiceScreen === 'sentence'">
          <div class="page-title">英语纠音·句子</div>

          <main class="practice-main sentence-main">
            <p class="hint-text">请朗读以下单词，可以逐个点亮单词</p>
            <div class="sentence-text" aria-label="当前朗读句子">
              <button
                v-for="(word, index) in currentLineWords"
                :key="`${word.text}-${index}`"
                class="word-chip"
                :class="{ scored: hasWordResult(word.normalized) }"
                @click="openWordPractice(index)"
              >
                {{ word.text }}
              </button>
            </div>

            <div
              v-if="recordStatus === 'done' && currentResult"
              class="line-result"
            >
              <p class="score-label">本句得分</p>
              <p class="score-number">
                {{ currentResult.overall }}<span>分</span>
              </p>
            </div>
          </main>

          <footer class="practice-footer">
            <button
              class="mic-btn"
              :class="{ recording: recordStatus === 'recording' }"
              :disabled="recordStatus === 'scoring'"
              @click="handleRecordClick"
            ></button>

            <p class="record-hint">{{ recordHint }}</p>
            <p v-if="!recorderReady" class="recorder-status">
              {{ recorderMessage }}
            </p>

            <div v-if="recordStatus === 'done'" class="line-actions">
              <button class="pill-btn light" @click="retryLine">
                再试一次
              </button>
              <button class="pill-btn" @click="goNextLine">
                {{ isLastLineInSegment ? "继续绘本" : "下一句" }}
              </button>
            </div>
          </footer>
        </template>

        <template v-else-if="practiceScreen === 'sentenceResult'">
          <main class="sentence-result-stage">
            <section class="sentence-score-panel" v-if="currentResult">
              <div
                class="score-ring"
                :style="{
                  background: scoreRingBackground(currentResult.overall),
                }"
              >
                <div class="score-ring-inner">
                  <strong>{{ currentResult.overall }}</strong
                  ><span>分</span>
                  <p>总分</p>
                </div>
              </div>

              <div class="score-breakdown">
                <div
                  v-for="item in sentenceScoreItems"
                  :key="item.key"
                  class="score-meter"
                >
                  <span class="meter-label">{{ item.label }}</span>
                  <div class="meter-track">
                    <i :style="{ width: item.percent + '%' }"></i>
                  </div>
                  <strong>{{ item.value }}</strong
                  ><span class="meter-unit">%</span>
                </div>

                <div class="speed-meter">
                  <span class="meter-label">语速</span>
                  <strong>{{ sentenceSpeed }}</strong>
                  <span class="speed-unit">词/分钟</span>
                </div>
              </div>
            </section>

            <section class="sentence-word-panel">
              <p>点击单词, 查看单词和音素发音分</p>
              <div class="result-word-list">
                <button
                  v-for="(word, index) in currentLineWords"
                  :key="`${word.text}-${index}`"
                  class="result-word-chip"
                  @click="openSentenceWordResult(index)"
                >
                  {{ word.text }}
                </button>
              </div>
            </section>

            <div class="sentence-result-actions">
              <button class="pill-btn wide" @click="retryLine">再试一次</button>
              <button class="pill-btn wide" @click="goNextLine">
                {{ isLastLineInSegment ? "下一页" : "下一句" }}
              </button>
            </div>
          </main>
        </template>

        <template v-else-if="practiceScreen === 'wordRecord'">
          <button class="word-back-btn" @click="backToSentence">
            ‹ 返回句子
          </button>
          <div class="page-title">英语纠音·单词</div>

          <main class="practice-main word-main">
            <p class="hint-text">请朗读以下单词</p>
            <h1 class="target-word">{{ currentWord.text }}</h1>
          </main>

          <footer class="practice-footer">
            <button class="swap-btn" @click="changeWord">
              <img class="swap-icon" src="../assets/swap-word.png" alt="" />
              <span>换个词</span>
            </button>

            <button
              class="mic-btn"
              :class="{ recording: recordStatus === 'recording' }"
              :disabled="recordStatus === 'scoring'"
              @click="handleRecordClick"
            ></button>

            <p class="record-hint">{{ recordHint }}</p>
            <p v-if="!recorderReady" class="recorder-status">
              {{ recorderMessage }}
            </p>
          </footer>
        </template>

        <template v-else>
          <button class="word-back-btn result-back-btn" @click="backToSentence">
            返回句子 ›
          </button>
          <main class="word-result-stage">
            <button class="play-record-btn" @click="playWordRecording">
              <span class="play-record-icon"></span>
              <span>播放录音</span>
            </button>

            <div class="summary-score word-score">
              <p>总分</p>
              <strong>{{ currentWordResult.overall }}</strong
              ><span>分</span>
            </div>

            <section class="word-result-hero">
              <h1 :class="{ weak: hasCurrentWordError }">
                {{ currentWord.text }}
              </h1>
              <p
                v-if="currentWordPhoneticSegments.length"
                class="word-phonetic-text"
              >
                <span class="phonetic-slash">/</span><span
                  v-for="(phoneme, index) in currentWordPhoneticSegments"
                  :key="`${phoneme.text}-${index}`"
                  :class="phoneme.scoreClass"
                >{{ phoneme.text }}</span><span class="phonetic-slash">/</span>
              </p>
              <div class="error-tabs">
                <button
                  :class="{ active: wordResultTab === 'error' }"
                  @click="wordResultTab = 'error'"
                >
                  发音偏误
                </button>
                <button
                  :class="{ active: wordResultTab === 'score' }"
                  @click="wordResultTab = 'score'"
                >
                  评分结果
                </button>
              </div>
            </section>

            <div class="phoneme-table">
              <div class="phoneme-row header">
                <span>音标</span>
                <span>实际发音</span>
                <span>{{ wordResultTab === "score" ? "评分结果" : "发音判断" }}</span>
              </div>
              <div
                v-for="(phoneme, index) in currentWordResult.phonemes"
                :key="`${phoneme.expected}-${index}`"
                class="phoneme-row"
                :class="{ weak: wordResultTab === 'error' && !phoneme.correct }"
              >
                <span>{{ phoneme.expected }}</span>
                <span>{{ phoneme.actual }}</span>
                <strong v-if="wordResultTab === 'score'">
                  {{ phoneme.pronunciation }}
                </strong>
                <strong v-else>{{ phoneme.statusText }}</strong>
              </div>
            </div>

            <div class="word-result-actions">
              <button class="pill-btn wide" @click="retryWord">再试一次</button>
              <button class="pill-btn wide" @click="changeWord">换个词</button>
            </div>
          </main>
        </template>
      </section>

      <section
        v-else-if="mode === 'review'"
        key="review"
        class="review-stage"
        :style="reviewStageStyle"
      >
        <div class="review-scrim"></div>
        <main class="review-content" v-if="currentReviewQuestion">
          <p class="review-progress">
            {{ reviewQuestionIndex + 1 }} / {{ validReviewQuestions.length }}
          </p>
          <h1 class="review-question">
            {{ currentReviewQuestion.question }}
          </h1>

          <div class="review-options">
            <button
              v-for="option in currentReviewOptions"
              :key="option.id"
              class="review-option"
              :class="getReviewOptionClass(option)"
              @click="toggleReviewAnswer(option.id)"
            >
              <span class="review-option-id">{{ option.id }}.</span>
              <span>{{ option.text }}</span>
            </button>
          </div>

          <div v-if="reviewFeedbackType" class="review-feedback-bubble">
            <img
              v-if="reviewFeedbackType === 'correct'"
              src="../assets/qa-right.png"
              alt="correct"
            />
            <img
              v-else-if="reviewFeedbackType === 'wrong'"
              src="../assets/qa-wrong.png"
              alt="Try Again"
            />
          </div>
        </main>

        <button
          class="review-next-btn"
          aria-label="验证答案"
          @click="validateReviewAnswer"
        >
          <img src="../assets/page-next-arrow.png" alt="下一页" />
        </button>
      </section>

      <section v-else key="summary" class="summary-stage">
        <div class="summary-score">
          <p>总分</p>
          <strong>{{ totalScore }}</strong
          ><span>分</span>
        </div>

        <div class="summary-highlight">
          {{ bestLineText }}
        </div>

        <div class="summary-table">
          <div class="summary-row header">
            <span>句子</span>
            <span>得分</span>
            <span>录音</span>
          </div>
          <div
            v-for="result in results"
            :key="result.lineId"
            class="summary-row"
            :class="{ weak: result.overall < 70 }"
          >
            <span>{{ result.text }}</span>
            <span>{{ result.overall }}</span>
            <button @click="replayRecord(result.recordId)">播放</button>
          </div>
        </div>

        <div class="summary-actions">
          <button class="pill-btn light" @click="restartPractice">
            再练一次
          </button>
          <button class="pill-btn" @click="handleSummaryComplete">
            {{ hasReviewQuestions ? "课后回顾" : "完成" }}
          </button>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import { _17kouyu } from "@/components/iload_es";
import {
  completeStoryPractice,
  getStoryPractice,
  saveScore,
  saveStoryLineResult,
} from "@/api/book";
import { ensureWechatOpenid } from "@/utils/wechat-auth";

const Config = {
  appKey: "17769972980006ff",
  secretKey: "2f5b9a61269e1ec717a331cf8a8846eb",
};

export default {
  name: "StoryPractice",
  data() {
    return {
      loading: true,
      loadError: "",
      bookId: null,
      story: null,
      user: null,
      mode: "story",
      practiceScreen: "sentence",
      activeRecordKind: "sentence",
      segmentIndex: 0,
      currentLineIndex: 0,
      selectedWordIndex: 0,
      sessionId: "",
      startedAt: "",
      results: [],
      wordResults: [],
      recorder: null,
      lastRecordId: "",
      recordStatus: "idle",
      recordTimer: null,
      recorderReady: false,
      recorderMessage: "录音机初始化中...",
      recorderReadyTimer: null,
      recordSeconds: 0,
      wordAudioPlayer: null,
      storyAudioPlayer: null,
      storyAudioPlaying: false,
      storyAudioAnimStep: 1,
      storyAudioAnimTimer: null,
      wordResultTab: "error",
      storyScreen: "cover",
      storyPageIndex: 0,
      savingResult: false,
      reviewQuestionIndex: 0,
      selectedReviewAnswers: [],
      reviewFeedbackType: "",
      reviewSubmitted: false,
    };
  },
  computed: {
    currentSegment() {
      if (!this.story || !this.story.storySegments)
        return { storyPages: [], lines: [] };
      return (
        this.story.storySegments[this.segmentIndex] || {
          storyPages: [],
          lines: [],
        }
      );
    },
    currentLines() {
      return this.currentSegment.lines || [];
    },
    storyPages() {
      return Array.isArray(this.currentSegment.storyPages)
        ? this.currentSegment.storyPages
        : [];
    },
    hasAnyStoryPages() {
      return Boolean(
        this.story &&
          Array.isArray(this.story.storySegments) &&
          this.story.storySegments.some(
            (segment) =>
              Array.isArray(segment.storyPages) && segment.storyPages.length > 0
          )
      );
    },
    hasCover() {
      return Boolean(
        this.story && this.story.cover && String(this.story.cover).trim()
      );
    },
    hasStoryPages() {
      return this.storyPages.length > 0;
    },
    currentStoryPage() {
      return this.storyPages[this.storyPageIndex] || { assets: [] };
    },
    currentStoryPageAudioUrl() {
      return (this.currentStoryPage && this.currentStoryPage.audioUrl) || "";
    },
    showStoryAudioStep2() {
      if (!this.storyAudioPlaying) return true;
      return this.storyAudioAnimStep >= 2;
    },
    showStoryAudioStep3() {
      if (!this.storyAudioPlaying) return true;
      return this.storyAudioAnimStep >= 3;
    },
    storyPageStageStyle() {
      const image =
        this.storyScreen === "cover" && this.hasCover
          ? this.story.cover
          : this.currentStoryPage.backgroundUrl;
      return image ? { backgroundImage: `url(${image})` } : {};
    },
    validReviewQuestions() {
      if (!this.story || !Array.isArray(this.story.reviewQuestions)) return [];
      return this.story.reviewQuestions
        .map((question) => ({
          ...question,
          type: question.type === "multiple" ? "multiple" : "single",
          options: (question.options || []).filter(
            (option) => option.id && option.text
          ),
          answers: Array.isArray(question.answers)
            ? question.answers.filter(Boolean)
            : [],
        }))
        .filter(
          (question) =>
            question.question &&
            question.options.length >= 2 &&
            question.answers.length > 0
        );
    },
    hasReviewQuestions() {
      return this.validReviewQuestions.length > 0;
    },
    currentReviewQuestion() {
      return this.validReviewQuestions[this.reviewQuestionIndex] || null;
    },
    currentReviewOptions() {
      return this.currentReviewQuestion
        ? this.currentReviewQuestion.options
        : [];
    },
    currentReviewAnswers() {
      return this.currentReviewQuestion
        ? this.currentReviewQuestion.answers
        : [];
    },
    reviewStageStyle() {
      const image =
        this.currentReviewQuestion &&
        this.currentReviewQuestion.backgroundImage;
      return image ? { backgroundImage: `url(${image})` } : {};
    },
    currentLine() {
      return this.currentLines[this.currentLineIndex] || { text: "" };
    },
    currentLineWords() {
      return this.extractWords(this.currentLine.text);
    },
    currentWord() {
      return (
        this.currentLineWords[this.selectedWordIndex] || {
          text: "",
          normalized: "",
        }
      );
    },
    currentWordPhonetic() {
      return this.currentWordResult.phonetic || "";
    },
    currentWordPhoneticSegments() {
      const phonemes = this.currentWordResult.phonemes || [];
      const segments = phonemes
        .map((item) => ({
          text: this.unwrapPhoneme(item.expected),
          scoreClass: this.getPhonemeScoreClass(item.pronunciation),
        }))
        .filter((item) => item.text);

      if (segments.length) return segments;
      return this.unwrapPhoneme(this.currentWordPhonetic)
        .split("")
        .filter(Boolean)
        .map((text) => ({
          text,
          scoreClass: "",
        }));
    },
    hasCurrentWordError() {
      return this.currentWordResult.phonemes.some((item) => !item.correct);
    },
    currentResult() {
      return this.results.find(
        (result) => result.lineId === this.currentLine.id
      );
    },
    currentWordResult() {
      return (
        this.wordResults.find(
          (result) => result.key === this.currentWordKey
        ) || {
          overall: 0,
          phonetic: "",
          phonemes: this.buildFallbackPhonemes(
            this.currentWord.normalized,
            true
          ),
        }
      );
    },
    currentWordKey() {
      return `${this.currentLine.id}:${this.currentWord.normalized}`;
    },
    isLastLineInSegment() {
      return this.currentLineIndex >= this.currentLines.length - 1;
    },
    totalScore() {
      if (!this.results.length) return 0;
      const sum = this.results.reduce((total, item) => total + item.overall, 0);
      return Math.round(sum / this.results.length);
    },
    bestLineText() {
      if (!this.results.length) return "";
      const best = this.results.reduce((current, item) => {
        return item.overall > current.overall ? item : current;
      }, this.results[0]);
      return best.text;
    },
    recordHint() {
      if (this.recordStatus === "recording")
        return `正在录音... ${this.recordSeconds}s`;
      if (this.recordStatus === "scoring") return "正在评测中...";
      if (this.recordStatus === "done") return "评测完成";
      if (!this.recorderReady) return "录音机准备中";
      return "点击麦克风 开始录音";
    },
    sentenceScoreItems() {
      if (!this.currentResult) return [];
      return [
        {
          key: "pronunciation",
          label: "发音",
          value: this.currentResult.pronunciation,
          percent: this.toPercent(this.currentResult.pronunciation),
        },
        {
          key: "fluency",
          label: "流畅",
          value: this.currentResult.fluency,
          percent: this.toPercent(this.currentResult.fluency),
        },
        {
          key: "integrity",
          label: "完整",
          value: this.currentResult.integrity,
          percent: this.toPercent(this.currentResult.integrity),
        },
      ];
    },
    sentenceSpeed() {
      if (!this.currentResult) return "000";
      return this.formatSpeed(this.currentResult.speed);
    },
  },
  created() {
    this.bookId = this.$route.params.bookId || this.$route.params.id;
    this.sessionId = `story-session-${Date.now()}`;
    this.startedAt = new Date().toISOString();
    this.initPage();
  },
  mounted() {
    this.initRecorder();
  },
    beforeDestroy() {
    this.clearRecordTimer();
    this.clearRecorderReadyTimer();
    this.clearStoryAudioAnimTimer();
    if (this.recorder) {
      this.recorder = null;
    }
    this.stopWordAudio();
    this.stopStoryAudio();
  },
  methods: {
    async initPage() {
      this.loading = true;
      this.loadError = "";

      try {
        await this.ensureWechatUser();
        const res = await getStoryPractice(this.bookId);
        this.story = res.data;
        this.mode = "story";
        this.practiceScreen = "sentence";
        this.activeRecordKind = "sentence";
        this.segmentIndex = 0;
        this.currentLineIndex = 0;
        this.selectedWordIndex = 0;
        this.initStoryScreen();
        this.stopStoryAudio();
        this.results = [];
        this.wordResults = [];
        this.resetReviewState();
        this.preloadStoryPageImages();
        if (!this.hasAnyStoryPages) {
          this.enterCurrentSegment();
        }
      } catch (error) {
        this.loadError = error.message || "加载失败，请重试";
      } finally {
        this.loading = false;
      }
    },
    preloadStoryPageImages() {
      if (!this.story || !Array.isArray(this.story.storySegments)) return;
      this.story.storySegments.forEach((segment) => {
        (segment.storyPages || []).forEach((page) => {
          if (page && page.backgroundUrl) {
            const img = new Image();
            img.src = page.backgroundUrl;
          }
        });
      });
    },
    async ensureWechatUser() {
      this.user = await ensureWechatOpenid(this.$route);
    },
    initRecorder() {
      if (this.recorder) return;

      try {
        this.recorder = new _17kouyu.IRecorder({
          id: "iRecorder",
          mode: 2,
          debug: true,
          appKey: Config.appKey,
          secretKey: Config.secretKey,
          onFlashLoad: (code) => {
            if (String(code) === "50000") {
              this.recorderMessage = "录音机加载完成，正在连接服务...";
            }
            this.checkRecorderReady();
          },
          onConnectorStatusChange: (code) => {
            if (String(code) === "50100") {
              this.recorderMessage = "录音服务已连接";
              this.checkRecorderReady();
            } else if (String(code) === "50103" || String(code) === "50104") {
              this.recorderMessage = "录音服务连接失败，请检查网络";
            } else if (String(code) === "50109") {
              this.recorderMessage = "正在连接录音服务...";
            }
          },
          onMicStatusChange: (code) => {
            if (String(code) === "50001") {
              this.recorderMessage = "麦克风已授权";
              this.checkRecorderReady();
            } else if (String(code) === "50002") {
              this.recorderReady = false;
              this.recorderMessage = "麦克风未授权，请允许浏览器访问麦克风";
            } else if (String(code) === "50003") {
              this.recorderReady = false;
              this.recorderMessage = "未检测到麦克风设备";
            }
          },
          onScore: (message) => {
            this.handleScoreResult(message);
          },
          onError: (message) => {
            console.error(message);
            this.recorderReady = false;
            this.recorderMessage = this.formatRecorderError(message);
            if (this.mode === "practice") {
              this.clearRecordTimer();
              this.recordStatus = "idle";
              alert("录音评测失败，请重试");
            }
          },
        });
        this.startRecorderReadyPolling();
      } catch (error) {
        console.error("初始化录音失败:", error);
        this.recorderReady = false;
        this.recorderMessage = "录音机初始化失败";
      }
    },
    startRecorderReadyPolling() {
      this.clearRecorderReadyTimer();
      let attempts = 0;
      this.recorderReadyTimer = setInterval(() => {
        attempts += 1;
        if (this.checkRecorderReady() || attempts >= 40) {
          this.clearRecorderReadyTimer();
          if (!this.recorderReady) {
            this.recorderMessage =
              this.recorderMessage || "录音机未准备好，请检查麦克风权限";
          }
        }
      }, 500);
    },
    checkRecorderReady() {
      if (this.recorder && this.recorder.canRecord) {
        this.recorderReady = true;
        this.recorderMessage = "";
        return true;
      }
      return false;
    },
    clearRecorderReadyTimer() {
      if (this.recorderReadyTimer) {
        clearInterval(this.recorderReadyTimer);
        this.recorderReadyTimer = null;
      }
    },
    formatRecorderError(message) {
      const text = typeof message === "string" ? message : "";
      if (text.indexOf("no audio device") > -1) {
        return "无法访问麦克风，请检查浏览器权限，并确认页面通过 HTTPS 打开";
      }
      if (text.indexOf("no audio context") > -1) {
        return "当前浏览器不支持 H5 录音";
      }
      if (text.indexOf("no audio media") > -1) {
        return "当前浏览器无法获取麦克风媒体流";
      }
      return text || "录音机初始化失败";
    },
    initStoryScreen() {
      this.storyPageIndex = 0;
      if (this.hasCover) {
        this.storyScreen = "cover";
        return;
      }
      if (this.hasAnyStoryPages) {
        this.storyScreen = "page";
        this.$nextTick(() => {
          this.playStoryAudio(this.currentStoryPage.audioUrl);
        });
        return;
      }
      this.storyScreen = "page";
    },
    startReadingPages() {
      this.enterCurrentSegment();
    },
    goNextStoryPage() {
      this.stopStoryAudio();
      if (this.storyPageIndex >= this.storyPages.length - 1) {
        this.enterPracticeOrNextSegment();
        return;
      }

      this.storyPageIndex += 1;
      this.$nextTick(() => {
        this.playStoryAudio(this.currentStoryPage.audioUrl);
      });
    },
    storyAssetStyle(asset) {
      return {
        left: `${this.toAssetPercent(asset.x)}%`,
        top: `${this.toAssetPercent(asset.y)}%`,
        width: `${this.toAssetWidth(asset.width)}%`,
      };
    },
    toAssetPercent(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return 0;
      return Math.min(100, Math.max(0, numeric));
    },
    toAssetWidth(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return 1;
      return Math.min(100, numeric);
    },
    handleStoryAssetClick(asset) {
      if (!asset || !asset.clickAudioEnabled) return;
      this.playStoryAudio(asset.audioUrl || this.currentStoryPage.audioUrl);
    },
    toggleStoryPageAudio() {
      if (this.storyAudioPlaying) {
        this.stopStoryAudio();
        return;
      }
      this.playStoryAudio(this.currentStoryPageAudioUrl);
    },
    playStoryAudio(url) {
      this.stopStoryAudio();
      if (!url) return;

      const player = new Audio(url);
      this.storyAudioPlayer = player;
      this.storyAudioPlaying = true;
      this.storyAudioAnimStep = 1;
      this.startStoryAudioAnimation();

      player.onended = () => {
        this.stopStoryAudio();
      };

      const playTask = player.play();
      if (playTask && playTask.catch) {
        playTask.catch(() => {
          this.stopStoryAudio();
        });
      }
    },
    startStoryAudioAnimation() {
      this.clearStoryAudioAnimTimer();
      const sequence = [1, 2, 3, 2];
      let index = 0;
      this.storyAudioAnimStep = sequence[index];
      this.storyAudioAnimTimer = setInterval(() => {
        index = (index + 1) % sequence.length;
        this.storyAudioAnimStep = sequence[index];
      }, 420);
    },
    clearStoryAudioAnimTimer() {
      if (this.storyAudioAnimTimer) {
        clearInterval(this.storyAudioAnimTimer);
        this.storyAudioAnimTimer = null;
      }
    },
    stopStoryAudio() {
      this.clearStoryAudioAnimTimer();
      this.storyAudioPlaying = false;
      this.storyAudioAnimStep = 1;

      if (!this.storyAudioPlayer) return;
      this.storyAudioPlayer.onended = null;
      this.storyAudioPlayer.pause();
      this.storyAudioPlayer.currentTime = 0;
      this.storyAudioPlayer = null;
    },
    enterPracticeOrNextSegment() {
      this.stopStoryAudio();
      if (this.currentLines.length > 0) {
        this.mode = "practice";
        this.practiceScreen = "sentence";
        this.activeRecordKind = "sentence";
        this.currentLineIndex = 0;
        this.selectedWordIndex = 0;
        this.recordStatus = this.currentResult ? "done" : "idle";
      } else {
        this.advanceToNextSegment();
      }
    },
    enterCurrentSegment() {
      this.stopStoryAudio();
      this.storyPageIndex = 0;
      this.currentLineIndex = 0;
      this.selectedWordIndex = 0;
      this.practiceScreen = "sentence";
      this.activeRecordKind = "sentence";
      this.recordStatus = this.currentResult ? "done" : "idle";

      if (this.hasStoryPages) {
        this.mode = "story";
        this.storyScreen = "page";
        this.$nextTick(() => {
          this.playStoryAudio(this.currentStoryPage.audioUrl);
        });
        return;
      }

      if (this.currentLines.length > 0) {
        this.mode = "practice";
        return;
      }

      this.advanceToNextSegment();
    },
    advanceToNextSegment() {
      if (this.segmentIndex < this.story.storySegments.length - 1) {
        this.segmentIndex += 1;
        this.enterCurrentSegment();
        return;
      }
      this.showSummary();
    },
    handleRecordClick() {
      if (this.recordStatus === "recording") {
        this.stopRecord();
        return;
      }

      if (!this.recorderReady) {
        this.requestRecorderAccess(true);
        return;
      }

      if (this.recordStatus === "idle" || this.recordStatus === "done") {
        this.startRecord();
      }
    },
    requestRecorderAccess(startAfterReady) {
      if (!this.recorder || typeof this.recorder.active !== "function") {
        this.recorderReady = false;
        this.recorderMessage =
          "当前浏览器不支持 H5 录音，请在微信内或支持麦克风的浏览器中打开";
        return;
      }

      this.recorderMessage = "正在请求麦克风权限...";
      this.recorder.active({
        onSuccess: () => {
          this.recorderReady = true;
          this.recorderMessage = "";
          if (startAfterReady) {
            this.startRecord();
          }
        },
        onError: (message) => {
          this.recorderReady = false;
          this.recordStatus = "idle";
          this.recorderMessage =
            message || "麦克风权限获取失败，请检查浏览器权限";
        },
      });
    },
    startRecord() {
      this.lastRecordId = "";
      this.recordStatus = "recording";
      this.recordSeconds = 0;
      this.activeRecordKind =
        this.practiceScreen === "wordRecord" ? "word" : "sentence";
      this.startRecordTimer();

      if (!this.recorder || !this.recorder.canRecord) {
        this.clearRecordTimer();
        this.recordStatus = "idle";
        this.recorderReady = false;
        this.recorderMessage =
          this.recorderMessage || "录音机尚未准备好，请检查麦克风权限";
        return;
      }

      const isWord = this.activeRecordKind === "word";
      this.recorder.record({
        duration: isWord ? 5000 : 10000,
        serverParams: {
          coreType: isWord ? "word.eval.promax" : "sent.eval",
          refText: isWord ? this.currentWord.normalized : this.currentLine.text,
          userId: this.user.openid || "guest",
          attachAudioUrl: 1,
        },
        onRecordIdGenerated: (code, message) => {
          this.lastRecordId = message.recordId;
        },
        onStop: () => {
          this.recordStatus = "scoring";
          this.clearRecordTimer();
        },
      });
    },
    stopRecord() {
      this.recordStatus = "scoring";
      this.clearRecordTimer();

      if (!this.recorder || !this.recorder.canRecord) {
        this.recordStatus = "idle";
        alert("录音机尚未准备好，请稍后再试");
        return;
      }

      this.recorder.stop({
        onStop: () => {
          this.recordStatus = "scoring";
        },
      });
    },
    startRecordTimer() {
      this.clearRecordTimer();
      this.recordTimer = setInterval(() => {
        this.recordSeconds += 1;
        const maxSeconds = this.activeRecordKind === "word" ? 5 : 10;
        if (this.recordSeconds >= maxSeconds) {
          this.stopRecord();
        }
      }, 1000);
    },
    clearRecordTimer() {
      if (this.recordTimer) {
        clearInterval(this.recordTimer);
        this.recordTimer = null;
      }
    },
    async handleScoreResult(message) {
      if (this.savingResult) return;
      this.savingResult = true;
      this.recordStatus = "scoring";
      this.clearRecordTimer();

      try {
        const messageObj =
          typeof message === "string" ? JSON.parse(message) : message;
        if (!messageObj || !messageObj.result || !this.lastRecordId) {
          throw new Error("未获取到有效评分结果");
        }

        if (this.activeRecordKind === "word") {
          await this.handleWordScoreResult(messageObj);
        } else {
          await this.handleSentenceScoreResult(messageObj);
        }
      } catch (error) {
        this.recordStatus = "idle";
        alert(error.message || "评分失败，请重试");
      } finally {
        this.savingResult = false;
      }
    },
    async handleSentenceScoreResult(messageObj) {
      const sdkResult = messageObj.result || {};
      const overall = this.readScore(sdkResult.overall);
      const result = {
        sessionId: this.sessionId,
        userId: this.user.userId,
        openid: this.user.openid,
        bookId: Number(this.bookId),
        segmentId: this.currentSegment.id,
        lineId: this.currentLine.id,
        text: this.currentLine.text,
        recordId: this.lastRecordId,
        overall,
        pronunciation: this.readScore(sdkResult.pronunciation),
        fluency: this.readScore(sdkResult.fluency),
        integrity: this.readScore(sdkResult.integrity),
        speed: this.readSpeed(sdkResult.speed),
        words: this.buildWordScores(overall),
        phonemes: [],
        raw: messageObj || {},
      };

      const res = await saveStoryLineResult(result);
      result.resultId = res.data.resultId;
      this.upsertResult(result);
      this.upsertSentenceWordResults(messageObj, result);
      this.recordStatus = "done";
      this.practiceScreen = "sentenceResult";
    },
    async handleWordScoreResult(messageObj) {
      const overall = Math.round(messageObj.result.overall || 0);
      const result = {
        key: this.currentWordKey,
        lineId: this.currentLine.id,
        text: this.currentWord.text,
        normalized: this.currentWord.normalized,
        recordId: this.lastRecordId,
        audioUrl: messageObj.audioUrl || "",
        overall,
        phonetic: this.extractWordPhonetic(messageObj),
        phonemes: this.parseWordPhonemes(messageObj, overall),
        raw: messageObj || {},
      };

      this.upsertWordResult(result);
      this.recordStatus = "done";
      this.practiceScreen = "wordResult";
      this.wordResultTab = "error";

      try {
        await saveScore({
          bookId: Number(this.bookId),
          wordId: this.buildSyntheticWordId(result.normalized),
          word: result.normalized,
          score: overall,
          recordId: this.lastRecordId,
          audioUrl: result.audioUrl,
          raw: messageObj || {},
        });
      } catch (error) {
        console.error("保存单词评分失败:", error);
      }
    },
    buildWordScores(overall) {
      return this.currentLineWords.map((word) => ({
        text: word.normalized,
        score: Math.max(
          50,
          Math.min(100, overall + Math.floor(Math.random() * 9) - 4)
        ),
      }));
    },
    extractWords(text) {
      return String(text || "")
        .split(/\s+/)
        .filter(Boolean)
        .map((token) => {
          const normalized = token
            .toLowerCase()
            .replace(/^[^a-z]+|[^a-z]+$/g, "");
          return {
            text: token,
            normalized,
          };
        })
        .filter((word) => word.normalized);
    },
    parseWordPhonemes(messageObj, overall) {
      const phonemes = this.getResultPhonemes(messageObj);
      if (phonemes.length) {
        return phonemes
          .map((item) => {
            const readType = this.normalizeReadType(item.readType);
            return {
              expected: this.wrapPhoneme(item.phoneme || ""),
              actual: this.wrapPhoneme(item.sound_like || item.phoneme || ""),
              pronunciation: this.readScore(item.pronunciation),
              readType,
              statusText: this.getPhonemeStatusText(readType),
              correct: readType === "0",
            };
          })
          .filter((item) => item.expected || item.actual);
      }

      const phonics = this.getResultPhonics(messageObj);
      if (phonics.length) {
        return phonics
          .map((item) => {
            if (typeof item === "string") {
              return {
                expected: this.wrapPhoneme(item),
                actual: this.wrapPhoneme(item),
                pronunciation: this.readScore(overall),
                readType: "0",
                statusText: "一致",
                correct: true,
              };
            }

            const expected =
              item.phonic ||
              item.phoneme ||
              item.phone ||
              item.text ||
              item.char ||
              item.symbol ||
              "";
            const actual =
              item.userPhonic ||
              item.userPhone ||
              item.actual ||
              item.read ||
              expected;
            const score = Number(item.score || item.overall || overall);
            return {
              expected: this.wrapPhoneme(expected),
              actual: this.wrapPhoneme(actual),
              pronunciation: this.readScore(score),
              readType: score >= 60 ? "0" : "6",
              statusText: score >= 60 ? "一致" : "混淆",
              correct: score >= 60,
            };
          })
          .filter((item) => item.expected);
      }

      const details =
        messageObj.result &&
        (messageObj.result.details || messageObj.result.phonemes);
      if (Array.isArray(details) && details.length) {
        return details
          .map((item) => {
            const expected =
              item.phoneme || item.phone || item.text || item.char || "";
            const actual = item.userPhone || item.actual || expected;
            const score = Number(item.score || item.overall || overall);
            return {
              expected: this.wrapPhoneme(expected),
              actual: this.wrapPhoneme(actual),
              pronunciation: this.readScore(score),
              readType: score >= 60 ? "0" : "6",
              statusText: score >= 60 ? "一致" : "混淆",
              correct: score >= 60,
            };
          })
          .filter((item) => item.expected);
      }
      return this.buildFallbackPhonemes(
        this.currentWord.normalized,
        overall >= 60
      );
    },
    getResultPhonics(messageObj) {
      const words = messageObj && messageObj.result && messageObj.result.words;
      if (!words) return [];

      const phonics = Array.isArray(words)
        ? words[0] && words[0].phonics
        : words.phonics;

      if (Array.isArray(phonics)) return phonics;
      if (typeof phonics === "string") {
        return phonics
          .replace(/^\/|\/$/g, "")
          .split(/\s+/)
          .filter(Boolean);
      }
      return [];
    },
    getResultPhonemes(messageObj) {
      const words = messageObj && messageObj.result && messageObj.result.words;
      if (!words) return [];

      const word = Array.isArray(words) ? words[0] : words;
      if (word && Array.isArray(word.phonemes)) return word.phonemes;
      return [];
    },
    normalizeReadType(value) {
      if (value === 1 / 2) return "1/2";
      return String(value);
    },
    getPhonemeStatusText(readType) {
      if (readType === "0") return "一致";
      if (readType === "1" || readType === "1/2" || readType === "0.5") {
        return "多读";
      }
      if (readType === "3") return "漏读";
      if (readType === "6") return "混淆";
      return "偏误";
    },
    extractWordPhonetic(messageObj) {
      const phonics = this.getResultPhonics(messageObj);
      if (!phonics.length) {
        const phonemes = this.getResultPhonemes(messageObj);
        const text = phonemes
          .map((item) => item.phoneme || "")
          .filter(Boolean)
          .map((item) => String(item).replace(/^\/|\/$/g, ""))
          .join("");
        return text ? `/${text}/` : "";
      }

      const text = phonics
        .map((item) => {
          if (typeof item === "string") return item;
          return (
            item.phonic ||
            item.phoneme ||
            item.phone ||
            item.text ||
            item.char ||
            item.symbol ||
            ""
          );
        })
        .filter(Boolean)
        .map((item) => String(item).replace(/^\/|\/$/g, ""))
        .join("");

      return text ? `/${text}/` : "";
    },
    buildFallbackPhonemes(word, correct) {
      const source =
        word === "fast" ? ["f", "æ", "s", "t"] : String(word || "").split("");
      return source.map((item) => ({
        expected: this.wrapPhoneme(item),
        actual: this.wrapPhoneme(item),
        pronunciation: correct ? 100 : 0,
        readType: correct ? "0" : "6",
        statusText: correct ? "一致" : "混淆",
        correct,
      }));
    },
    wrapPhoneme(value) {
      const text = String(value || "").replace(/^\/|\/$/g, "");
      return text ? `/${text}/` : "";
    },
    unwrapPhoneme(value) {
      return String(value || "").replace(/^\/|\/$/g, "");
    },
    getPhonemeScoreClass(score) {
      const value = Number(score || 0);
      if (!isFinite(value)) return "";
      if (value > 80) return "phonetic-score-high";
      if (value < 60) return "phonetic-score-low";
      return "";
    },
    buildSyntheticWordId(word) {
      return String(word || "")
        .split("")
        .reduce((total, char) => total + char.charCodeAt(0), 1);
    },
    readScore(value) {
      const score = Number(value || 0);
      if (!isFinite(score)) return 0;
      return Math.max(0, Math.min(100, Math.round(score)));
    },
    readSpeed(value) {
      const speed = Number(value || 0);
      if (!isFinite(speed)) return 0;
      return Math.max(0, Math.round(speed));
    },
    toPercent(value) {
      const score = Number(value || 0);
      if (!isFinite(score)) return 0;
      return Math.max(0, Math.min(100, score));
    },
    formatSpeed(value) {
      const text = String(this.readSpeed(value));
      if (text.length >= 3) return text;
      return new Array(4 - text.length).join("0") + text;
    },
    scoreRingBackground(value) {
      const percent = this.toPercent(value);
      return `conic-gradient(#7df28c 0 ${percent * 3.6}deg, transparent ${
        percent * 3.6
      }deg 360deg)`;
    },
    hasWordResult(word) {
      return this.wordResults.some(
        (result) =>
          result.lineId === this.currentLine.id && result.normalized === word
      );
    },
    upsertResult(result) {
      const index = this.results.findIndex(
        (item) => item.lineId === result.lineId
      );
      if (index >= 0) {
        this.results.splice(index, 1, result);
      } else {
        this.results.push(result);
      }
    },
    upsertWordResult(result) {
      const index = this.wordResults.findIndex(
        (item) => item.key === result.key
      );
      if (index >= 0) {
        this.wordResults.splice(index, 1, result);
      } else {
        this.wordResults.push(result);
      }
    },
    upsertSentenceWordResults(messageObj, lineResult) {
      const sdkWords = this.getResultWords(messageObj);
      this.currentLineWords.forEach((word, index) => {
        const sdkWord = this.findSdkWordResult(sdkWords, word, index);
        const overall = this.readScore(
          sdkWord.pronunciation || sdkWord.overall || word.score || lineResult.overall
        );
        const wordMessage = {
          audioUrl: messageObj.audioUrl || "",
          result: {
            overall,
            words: sdkWord,
          },
        };
        const result = {
          key: `${this.currentLine.id}:${word.normalized}`,
          lineId: this.currentLine.id,
          text: word.text,
          normalized: word.normalized,
          recordId: lineResult.recordId,
          audioUrl: messageObj.audioUrl || "",
          overall,
          phonetic: this.extractWordPhonetic(wordMessage),
          phonemes: this.parseWordPhonemes(wordMessage, overall),
          raw: wordMessage,
        };
        this.upsertWordResult(result);
      });
    },
    getResultWords(messageObj) {
      const words = messageObj && messageObj.result && messageObj.result.words;
      if (!words) return [];
      return Array.isArray(words) ? words : [words];
    },
    findSdkWordResult(words, currentWord, index) {
      const normalized = currentWord.normalized;
      return (
        words.find((item) => {
          const text = String(
            item.word || item.text || item.content || item.refText || ""
          )
            .toLowerCase()
            .replace(/^[^a-z]+|[^a-z]+$/g, "");
          return text && text === normalized;
        }) ||
        words[index] ||
        {}
      );
    },
    openSentenceWordResult(index) {
      this.selectedWordIndex = index;
      this.activeRecordKind = "word";
      this.recordStatus = "done";
      this.practiceScreen = "wordResult";
      this.wordResultTab = "error";
    },
    openWordPractice(index) {
      this.selectedWordIndex = index;
      this.practiceScreen = "wordRecord";
      this.activeRecordKind = "word";
      this.recordStatus = this.hasWordResult(this.currentWord.normalized)
        ? "done"
        : "idle";
    },
    backToSentence() {
      this.clearRecordTimer();
      this.practiceScreen = this.currentResult ? "sentenceResult" : "sentence";
      this.activeRecordKind = "sentence";
      this.recordStatus = this.currentResult ? "done" : "idle";
    },
    retryLine() {
      this.practiceScreen = "sentence";
      this.recordStatus = "idle";
      this.activeRecordKind = "sentence";
    },
    retryWord() {
      this.practiceScreen = "wordRecord";
      this.activeRecordKind = "word";
      this.recordStatus = "idle";
    },
    changeWord() {
      const words = this.currentLineWords;
      if (!words.length) return;
      this.selectedWordIndex = (this.selectedWordIndex + 1) % words.length;
      this.practiceScreen = "wordRecord";
      this.activeRecordKind = "word";
      this.recordStatus = this.hasWordResult(this.currentWord.normalized)
        ? "done"
        : "idle";
    },
    goNextLine() {
      if (!this.currentResult) return;

      if (!this.isLastLineInSegment) {
        this.currentLineIndex += 1;
        this.selectedWordIndex = 0;
        this.practiceScreen = this.currentResult
          ? "sentenceResult"
          : "sentence";
        this.activeRecordKind = "sentence";
        this.recordStatus = this.currentResult ? "done" : "idle";
        return;
      }

      this.advanceToNextSegment();
    },
    skipPracticeSegment() {
      if (this.recordStatus === "recording" || this.recordStatus === "scoring") {
        return;
      }

      this.clearRecordTimer();
      this.stopWordAudio();
      this.recordStatus = "idle";
      this.practiceScreen = "sentence";
      this.activeRecordKind = "sentence";
      this.advanceToNextSegment();
    },
    async showSummary() {
      this.mode = "summary";

      // 未做测评（totalScore 为 0）时不调用 complete 接口
      if (!this.totalScore) {
        return;
      }

      try {
        await completeStoryPractice({
          sessionId: this.sessionId,
          userId: this.user.userId,
          openid: this.user.openid,
          bookId: Number(this.bookId),
          startedAt: this.startedAt,
          completedAt: new Date().toISOString(),
          totalScore: this.totalScore,
          segmentResults: this.buildSegmentResults(),
        });
      } catch (error) {
        localStorage.setItem(
          `storyPracticePending:${this.sessionId}`,
          JSON.stringify({
            sessionId: this.sessionId,
            bookId: Number(this.bookId),
            totalScore: this.totalScore,
            results: this.results,
          })
        );
      }
    },
    buildSegmentResults() {
      return this.story.storySegments.map((segment) => {
        const lineResults = this.results.filter(
          (result) => result.segmentId === segment.id
        );
        const sum = lineResults.reduce(
          (total, item) => total + item.overall,
          0
        );
        return {
          segmentId: segment.id,
          averageScore: lineResults.length
            ? Math.round(sum / lineResults.length)
            : 0,
          lineResults: lineResults.map((result) => ({
            lineId: result.lineId,
            resultId: result.resultId,
            text: result.text,
            overall: result.overall,
            recordId: result.recordId,
          })),
        };
      });
    },
    replayRecord(recordId) {
      if (!this.recorder || !recordId) return;
      this.recorder.startReplay({
        recordId,
        onStart: () => {},
        onStop: () => {},
      });
    },
    playWordRecording() {
      const result = this.currentWordResult;
      if (!result || (!result.audioUrl && !result.recordId)) return;

      if (!result.audioUrl) {
        this.replayRecord(result.recordId);
        return;
      }

      this.stopWordAudio();
      this.wordAudioPlayer = new Audio(result.audioUrl);
      const playTask = this.wordAudioPlayer.play();
      if (playTask && playTask.catch) {
        playTask.catch(() => {
          this.replayRecord(result.recordId);
        });
      }
    },
    stopWordAudio() {
      if (!this.wordAudioPlayer) return;
      this.wordAudioPlayer.pause();
      this.wordAudioPlayer = null;
    },
    restartPractice() {
      this.sessionId = `story-session-${Date.now()}`;
      this.startedAt = new Date().toISOString();
      this.segmentIndex = 0;
      this.currentLineIndex = 0;
      this.selectedWordIndex = 0;
      this.results = [];
      this.wordResults = [];
      this.resetReviewState();
      this.recordStatus = "idle";
      this.mode = "story";
      this.practiceScreen = "sentence";
      this.activeRecordKind = "sentence";
      this.initStoryScreen();
      this.stopStoryAudio();
      if (!this.hasAnyStoryPages) {
        this.enterCurrentSegment();
      }
    },
    resetReviewState() {
      this.reviewQuestionIndex = 0;
      this.selectedReviewAnswers = [];
      this.reviewFeedbackType = "";
      this.reviewSubmitted = false;
    },
    handleSummaryComplete() {
      if (this.hasReviewQuestions) {
        this.startReview();
        return;
      }
      this.finishPractice();
    },
    startReview() {
      this.resetReviewState();
      this.mode = "review";
    },
    toggleReviewAnswer(optionId) {
      if (!this.currentReviewQuestion || !optionId) return;
      this.reviewSubmitted = false;
      this.reviewFeedbackType = "";

      if (this.currentReviewQuestion.type === "single") {
        this.selectedReviewAnswers =
          this.selectedReviewAnswers[0] === optionId ? [] : [optionId];
        return;
      }

      if (this.selectedReviewAnswers.indexOf(optionId) > -1) {
        this.selectedReviewAnswers = this.selectedReviewAnswers.filter(
          (answer) => answer !== optionId
        );
      } else {
        this.selectedReviewAnswers = this.selectedReviewAnswers.concat(
          optionId
        );
      }
    },
    validateReviewAnswer() {
      if (!this.currentReviewQuestion) {
        this.finishPractice();
        return;
      }

      this.reviewSubmitted = true;
      if (!this.selectedReviewAnswers.length) {
        this.reviewFeedbackType = "wrong";
        return;
      }

      if (!this.isReviewAnswerCorrect()) {
        this.reviewFeedbackType = "wrong";
        return;
      }

      this.reviewFeedbackType = "correct";
      setTimeout(() => {
        this.goNextReviewQuestion();
      }, 800);
    },
    isReviewAnswerCorrect() {
      const selected = this.selectedReviewAnswers.slice().sort();
      const answers = this.currentReviewAnswers.slice().sort();
      if (selected.length !== answers.length) return false;
      return selected.every((answer, index) => answer === answers[index]);
    },
    goNextReviewQuestion() {
      if (this.reviewQuestionIndex >= this.validReviewQuestions.length - 1) {
        this.finishPractice();
        return;
      }

      this.reviewQuestionIndex += 1;
      this.selectedReviewAnswers = [];
      this.reviewFeedbackType = "";
      this.reviewSubmitted = false;
    },
    getReviewOptionClass(option) {
      const selected = this.selectedReviewAnswers.indexOf(option.id) > -1;
      return {
        selected,
      };
    },
    finishPractice() {
      this.$router.push("/books");
    },
  },
};
</script>

<style scoped>
.story-practice-page {
  min-height: 100vh;
  background: #fff;
  color: #050505;
  overflow: hidden;
}

.recorder-host {
  position: fixed;
  width: 1px;
  height: 1px;
  left: -10px;
  top: -10px;
  opacity: 0;
  pointer-events: none;
}

.center-state,
.practice-stage,
.review-stage,
.summary-stage {
  min-height: 100vh;
}

.story-page-stage {
  min-height: 176vw;
}

.center-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  color: #333;
}

.story-page-stage {
  position: relative;
  width: 100vw;
  min-height: max(176vw, 100vh);
  overflow: hidden;
  background-color: #dceaa0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.story-page-canvas {
  position: relative;
  width: 100%;
  height: 176vw;
  background-position: center center;
  background-size: 100% auto;
  background-repeat: no-repeat;
}

.story-cover-fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 30%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.92)
  );
}

.start-reading-btn {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 54px;
  height: 64px;
  border: 2px solid rgba(0, 0, 0, 0.58);
  border-radius: 34px;
  background: rgba(255, 255, 255, 0.78);
  color: #25c970;
  font-size: 28px;
  font-weight: 500;
  letter-spacing: 0;
}

.story-page-asset {
  position: absolute;
  border: 0;
  padding: 0;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  animation-duration: 560ms;
  animation-fill-mode: both;
  animation-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
}

.story-page-asset img {
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
}

.enter-left {
  animation-name: enterFromLeft;
}

.enter-right {
  animation-name: enterFromRight;
}

.enter-top {
  animation-name: enterFromTop;
}

.enter-bottom {
  animation-name: enterFromBottom;
}

@keyframes enterFromLeft {
  from {
    opacity: 0;
    transform: translateX(-42px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes enterFromRight {
  from {
    opacity: 0;
    transform: translateX(42px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes enterFromTop {
  from {
    opacity: 0;
    transform: translateY(-42px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes enterFromBottom {
  from {
    opacity: 0;
    transform: translateY(42px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.page-next-btn {
  position: absolute;
  right: 14px;
  bottom: 30px;
  width: 48px;
  border: 0;
  padding: 0;
  background: transparent;
  z-index: 2;
}

.page-audio-btn {
  position: absolute;
  left: 14px;
  bottom: 40px;
  width: 38px;
  height: 38px;
  border: 0;
  padding: 0;
  background: transparent;
  z-index: 2;
  -webkit-tap-highlight-color: transparent;
}

.page-audio-icon {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
}

.page-audio-step {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.18s ease;
}

.page-audio-step1 {
  opacity: 1;
}

.page-audio-step2.visible,
.page-audio-step3.visible {
  opacity: 1;
}

.page-next-btn img {
  display: block;
  width: 100%;
  height: auto;
}

.review-stage {
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  background-color: #d7e581;
  background-position: center;
  background-size: cover;
  color: #1a4325;
  font-family: Georgia, "Times New Roman", Times, serif;
}

.review-scrim {
  position: absolute;
  inset: 0;
  background: rgba(226, 235, 111, 0.26);
  pointer-events: none;
}

.review-content {
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  min-height: 100vh;
  padding: 20vh 11vw 120px 12vw;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.18);
}

.review-progress {
  margin: 0 0 16px;
  color: rgba(26, 67, 37, 0.78);
  font-size: clamp(14px, 4vw, 16px);
  font-weight: 700;
}

.review-question {
  margin: 0;
  max-width: 100%;
  color: #1a4325;
  font-size: clamp(18px, 5.5vw, 22px);
  line-height: 2;
  font-weight: 700;
  word-break: break-word;
}

.review-options {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  margin-top: 8px;
  max-width: 100%;
}

.review-option {
  display: inline-flex;
  align-items: flex-start;
  max-width: 100%;
  min-height: 0;
  padding: 4px 18px;
  border: 0;
  background: transparent;
  color: #1a4325;
  font-size: clamp(18px, 5.5vw, 22px);
  line-height: 2;
  font-weight: 700;
  text-align: left;
  word-break: break-word;
}

.review-option.selected {
  background: transparent url("../assets/qa-selected.png") center / 100% 100%
    no-repeat;
}

.review-option-id {
  flex: 0 0 auto;
  margin-right: 6px;
}

.review-feedback-bubble {
  position: fixed;
  z-index: 2;
  left: 28vw;
  bottom: 22vh;
  pointer-events: none;
}

.review-feedback-bubble img {
  display: block;
  width: clamp(140px, 42vw, 200px);
  height: auto;
}

.review-next-btn {
  position: fixed;
  z-index: 2;
  right: 14px;
  bottom: 20px;
  width: 58px;
  border: 0;
  padding: 0;
  background: transparent;
}

.review-next-btn img {
  display: block;
  width: 100%;
  height: auto;
}

.practice-stage {
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 47px 26px 42px;
}

.page-title {
  text-align: center;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 800;
}

.skip-segment-btn {
  position: absolute;
  right: 22px;
  top: 22px;
  z-index: 2;
  height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  color: #666;
  font-size: 14px;
  font-weight: 700;
}

.word-back-btn {
  position: absolute;
  left: 22px;
  top: 22px;
  height: 30px;
  padding: 0 8px;
  border: none;
  background: transparent;
  color: #333;
  font-size: 14px;
  font-weight: 700;
}

.result-back-btn {
  top: 22px;
}

.practice-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.sentence-main {
  padding-top: 104px;
}

.word-main {
  padding-top: 104px;
}

.hint-text {
  margin: 0;
  color: #050505;
  font-size: 17px;
  line-height: 1.45;
  font-weight: 700;
}

.sentence-text {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 10px;
  margin-top: 66px;
  max-width: 100%;
}

.word-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #050505;
  font-size: 39px;
  line-height: 1.25;
  font-weight: 400;
}

.word-chip.scored {
  color: #30cf78;
}

.target-word {
  margin: 55px 0 0;
  color: #050505;
  font-size: 57px;
  line-height: 1.1;
  font-weight: 400;
}

.line-result {
  margin-top: 34px;
}

.score-label {
  margin: 0;
  color: #666;
  font-size: 12px;
}

.score-number {
  margin: 4px 0 0;
  color: #30cf78;
  font-size: 34px;
  font-weight: 700;
}

.score-number span {
  font-size: 14px;
}

.practice-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 207px;
}

.swap-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 107px;
  height: 29px;
  margin-bottom: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #e7f8ef;
  color: #1dc46d;
  font-size: 16px;
  font-weight: 800;
}

.swap-icon {
  width: 20px;
  height: 20px;
  display: block;
  flex: 0 0 auto;
}

.mic-btn {
  width: 82px;
  height: 82px;
  border: none;
  border-radius: 50%;
  background: transparent url("../assets/record-mic.png") center / contain
    no-repeat;
  position: relative;
}

.mic-btn:before {
  display: none;
}

.mic-btn:after {
  display: none;
}

.mic-btn.recording {
  transform: scale(0.96);
}

.record-hint {
  margin: 25px 0 0;
  color: #666;
  font-size: 14px;
  line-height: 1.3;
  font-weight: 700;
}

.recorder-status {
  margin: 8px 0 0;
  max-width: 280px;
  color: #999;
  font-size: 11px;
  line-height: 1.5;
  text-align: center;
}

.line-actions,
.summary-actions {
  display: flex;
  gap: 18px;
  margin-top: 18px;
}

.pill-btn {
  min-width: 108px;
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #30cf78;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.pill-btn.light {
  background: #e7f8ef;
  color: #26c76f;
}

.pill-btn.wide {
  min-width: 132px;
  height: 50px;
  font-size: 17px;
}

.sentence-result-stage {
  min-height: calc(100vh - 89px);
  display: flex;
  flex-direction: column;
  margin: -47px -26px -42px;
  background: #fff;
}

.sentence-score-panel {
  box-sizing: border-box;
  min-height: 328px;
  padding: 48px 34px 28px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.score-ring {
  width: 138px;
  height: 138px;
  border-radius: 50%;
  padding: 10px;
  transform: rotate(-132deg);
}

.score-ring-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  transform: rotate(132deg);
  color: #050505;
}

.score-ring-inner strong {
  font-size: 47px;
  line-height: 1;
  font-weight: 800;
}

.score-ring-inner span {
  align-self: flex-end;
  margin: 0 0 6px 4px;
  color: #555;
  font-size: 18px;
  line-height: 1;
}

.score-ring-inner p {
  flex: 0 0 100%;
  margin: 15px 0 0;
  color: #666;
  font-size: 20px;
  line-height: 1;
  text-align: center;
}

.score-breakdown {
  width: 100%;
  max-width: 318px;
  margin-top: 36px;
}

.score-meter {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 48px 22px;
  align-items: center;
  column-gap: 8px;
  min-height: 28px;
}

.meter-label {
  color: #050505;
  font-size: 19px;
  line-height: 1;
  font-weight: 800;
  white-space: nowrap;
}

.meter-track {
  height: 11px;
  overflow: hidden;
  background: #edf0f4;
}

.meter-track i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #66eda0 0%, #9cf878 100%);
}

.score-meter strong,
.speed-meter strong {
  color: #050505;
  font-size: 28px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
}

.meter-unit,
.speed-unit {
  color: #b8bac7;
  font-size: 25px;
  line-height: 1;
}

.speed-meter {
  display: flex;
  align-items: center;
  min-height: 31px;
  gap: 9px;
}

.speed-meter .meter-label {
  margin-right: 3px;
}

.speed-unit {
  font-size: 19px;
}

.sentence-word-panel {
  box-sizing: border-box;
  min-height: 177px;
  padding: 30px 38px;
  background: #f5f5f8;
}

.sentence-word-panel p {
  margin: 0;
  color: #b8bac7;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 700;
}

.result-word-list {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 24px;
}

.result-word-chip {
  min-width: 42px;
  height: 48px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: #30cf78;
  font-size: 33px;
  line-height: 1;
  font-weight: 400;
}

.sentence-result-actions {
  display: flex;
  justify-content: center;
  gap: 27px;
  margin-top: auto;
  padding: 38px 28px 57px;
}

.word-result-stage {
  min-height: calc(100vh - 89px);
  display: flex;
  flex-direction: column;
  position: relative;
  padding-top: 2px;
}

.summary-score p {
  margin: 0;
  font-size: 17px;
  line-height: 1.2;
  font-weight: 700;
}

.summary-score strong {
  font-size: 44px;
  line-height: 1.2;
  font-weight: 400;
}

.summary-score span {
  font-size: 18px;
}

.word-score {
  margin-top: 0;
}

.play-record-btn {
  position: absolute;
  top: 8px;
  right: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 133px;
  height: 37px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: #e7f8ef;
  color: #30cf78;
  font-size: 18px;
  line-height: 1;
  font-weight: 800;
}

.play-record-icon {
  width: 0;
  height: 0;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 10px solid #30cf78;
}

.word-result-hero {
  margin-top: 52px;
  text-align: center;
}

.word-result-hero h1 {
  margin: 0;
  color: #30cf78;
  font-size: 58px;
  line-height: 1.1;
  font-weight: 400;
}

.word-result-hero h1.weak {
  color: #d0021b;
}

.word-phonetic-text {
  margin: 14px 0 24px;
  color: #050505;
  font-size: 29px;
  line-height: 1.2;
}

.phonetic-score-high {
  color: #30cf78;
}

.phonetic-score-low {
  color: #d0021b;
}

.error-tabs {
  display: inline-grid;
  grid-template-columns: 1fr 1fr;
  width: 238px;
  height: 40px;
  overflow: hidden;
  border: 1px solid #30cf78;
  border-radius: 999px;
}

.error-tabs button {
  border: none;
  background: #fff;
  color: #555;
  font-size: 20px;
  font-weight: 800;
}

.error-tabs button.active {
  background: #30cf78;
  color: #fff;
}

.phoneme-table {
  margin: 48px auto 0;
  width: 100%;
  max-width: 288px;
  overflow: hidden;
  border-radius: 6px 6px 0 0;
}

.phoneme-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  min-height: 35px;
  background: #fff;
  color: #555;
  font-size: 20px;
  text-align: center;
}

.phoneme-row.header {
  background: #eceeef;
}

.phoneme-row:not(.header):nth-child(odd) {
  background: #eceeef;
}

.phoneme-row.header {
  min-height: 35px;
  color: #8d8d8d;
  font-size: 14px;
  font-weight: 800;
}

.phoneme-row strong {
  color: #050505;
  font-size: 20px;
  font-weight: 700;
}

.phoneme-row.weak span:nth-child(2),
.phoneme-row.weak strong {
  color: #f5a623;
}

.word-result-actions {
  display: flex;
  justify-content: space-between;
  gap: 27px;
  margin-top: auto;
  padding: 28px 7px 0;
}

.summary-stage {
  box-sizing: border-box;
  padding: 46px 30px 34px;
  display: flex;
  flex-direction: column;
}

.summary-highlight {
  margin: 30px 0 18px;
  color: #30cf78;
  font-size: 30px;
  line-height: 1.2;
  font-weight: 700;
  text-align: center;
}

.summary-table {
  flex: 1;
  overflow-y: auto;
  margin-top: 12px;
}

.summary-row {
  display: grid;
  grid-template-columns: 1fr 52px 54px;
  gap: 8px;
  align-items: center;
  min-height: 34px;
  padding: 6px 10px;
  margin-bottom: 6px;
  background: #f2f3f5;
  color: #333;
  font-size: 12px;
}

.summary-row.header {
  color: #999;
  font-weight: 600;
}

.summary-row.weak {
  background: #fff2f2;
}

.summary-row button {
  border: 1px solid #30cf78;
  border-radius: 999px;
  background: #fff;
  color: #30cf78;
  font-size: 11px;
  height: 24px;
}

.summary-actions {
  justify-content: center;
}

.scene-switch-enter-active {
  transition: opacity 260ms ease, transform 260ms ease;
}

.scene-switch-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.scene-switch-enter {
  opacity: 0;
  transform: translateY(12px);
}

.scene-switch-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

button {
  cursor: pointer;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/views/StoryPractice.vue